import CreativesTableV2 from './CreativesTableV2';
import { Button, Collapse, Form, Input, Layout, Select, DatePicker, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { StyledSider } from './styles';
import { connect } from 'dva';
import { FilterOutlined } from '@ant-design/icons';
import { FILTER_FIELDS_MAP_V2, LAST_UPDATED_RANGE } from './constants';
import { VIEW } from '../../constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { debounce } from 'lodash';

dayjs.extend(utc);

const CreativeTrackerV2 = ({ dispatch, filters, permissions, brandOptionsLoading }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');

  const debouncedBrandSearch = useMemo(
    () =>
      debounce((dispatch, value) => {
        dispatch({ type: 'creativeTrackerV2/updateBrandOptions', payload: value });
      }, 300),
    [dispatch]
  );
  const handleAutocompleteSearch = (value, key) => {
    setSearchValue(value);
    if (key === 'brand-crm-ids') {
      debouncedBrandSearch(dispatch, value);
    }
  };

  const handleDropdownVisibleChange = (open, key) => {
    if (!open && key === 'brand-crm-ids') {
      setSearchValue('');
      dispatch({ type: 'creativeTrackerV2/clearBrandOptions' });
    }
  };

  const getNotFoundContent = (key, loading, options, value) => {
    if (key === 'brand-crm-ids') {
      if (loading) return <Spin size="small" />;
      if (!loading && value?.length >= 1 && Array.isArray(options) && options.length === 0)
        return <span>No data found</span>;
      if (!value || value.length === 0) return <span>Start typing to search</span>;
      return <span />;
    }
    return <span />;
  };

  const submitFilters = (values) => {
    const newValues = { ...values };
    newValues['tracking-ids'] = (newValues['tracking-ids'] || [])
      .flatMap((val) => (typeof val === 'string' ? val.split(',') : [val]))
      .map((val) => parseInt(val, 10))
      .filter((num) => !isNaN(num));
    dispatch({ type: 'creativeTrackerV2/updateFilterSelected', payload: newValues });
    dispatch({ type: 'creativeTrackerV2/pageUpdate' });
  };

  const allowedFilters = Object.entries(filters || {}).filter(
    ([field]) =>
      permissions && typeof permissions.can === 'function' && permissions.can(VIEW, FILTER_FIELDS_MAP_V2[field])
  );

  const initialValues = {
    [LAST_UPDATED_RANGE]: filters[LAST_UPDATED_RANGE].selected,
  };

  return (
    <Layout>
      <StyledSider collapsible collapsed={collapsed} trigger={null} width={320}>
        {collapsed ? (
          <Button onClick={() => setCollapsed(false)}>
            <FilterOutlined />
          </Button>
        ) : (
          <Form form={form} onFinish={submitFilters} initialValues={initialValues}>
            <h1>
              <FilterOutlined /> Filter
            </h1>
            <Collapse>
              {allowedFilters.map(([key, field]) =>
                field.type ? (
                  <Collapse.Panel header={field.name} key={key}>
                    <Form.Item name={key} rules={field.rules}>
                      {field.type === 'input' && <Input placeholder="Enter value" />}
                      {field.type === 'multivalue-input' && (
                        <Select
                          mode="tags"
                          style={{ width: '100%' }}
                          tokenSeparators={[',']}
                          allowClear
                          placeholder={field.placeholder || 'Enter values'}
                        />
                      )}
                      {field.type === 'select' && (
                        <Select options={field.options} style={{ width: '100%' }} allowClear />
                      )}
                      {field.type === 'multiselect' && (
                        <Select
                          mode="multiple"
                          options={field.options}
                          style={{ width: '100%' }}
                          optionFilterProp={'label'}
                          allowClear
                        />
                      )}
                      {field.type === 'date' && (
                        <DatePicker.RangePicker
                          style={{ width: '100%' }}
                          presets={[
                            { label: 'Today', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
                            {
                              label: 'Last 2 Days',
                              value: [dayjs().startOf('day').subtract(1, 'day'), dayjs().endOf('day')],
                            },
                            {
                              label: 'Last 7 Days',
                              value: [dayjs().startOf('day').subtract(6, 'day'), dayjs().endOf('day')],
                            },
                            {
                              label: 'Last 30 Days',
                              value: [dayjs().startOf('day').subtract(29, 'day'), dayjs().endOf('day')],
                            },
                          ]}
                        />
                      )}
                      {field.type === 'autocomplete' && (
                        <Select
                          mode="multiple"
                          options={field.options}
                          style={{ width: '100%' }}
                          optionFilterProp={'label'}
                          onSearch={(value) => handleAutocompleteSearch(value, key)}
                          onClear={() => handleAutocompleteSearch('', key)}
                          onDropdownVisibleChange={(open) => handleDropdownVisibleChange(open, key)}
                          allowClear
                          notFoundContent={getNotFoundContent(key, brandOptionsLoading, field.options, searchValue)}
                        />
                      )}
                    </Form.Item>
                  </Collapse.Panel>
                ) : null
              )}
            </Collapse>
            <Form.Item style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button style={{ margin: 8 }} onClick={() => setCollapsed(true)}>
                Cancel
              </Button>
              <Button style={{ margin: 8 }} type="primary" htmlType="submit">
                Submit
              </Button>
            </Form.Item>
          </Form>
        )}
      </StyledSider>
      <CreativesTableV2 />
    </Layout>
  );
};

function mapStateToProps({ app, creativeTrackerV2, loading }) {
  return {
    pageInitLoading: loading.effects['creativeTrackerV2/pageInit'],
    pageUpdateLoading: loading.effects['creativeTrackerV2/pageUpdate'],
    brandOptionsLoading: loading.effects['creativeTrackerV2/updateBrandOptions'],
    assetLoading: loading.effects['creativeTrackerV2/fetchAssetDetails'],
    creatives: creativeTrackerV2.creatives,
    filters: creativeTrackerV2.filters,
    assets: creativeTrackerV2.assets,
    pathParams: app.pathParams,
    permissions: app.currentUser.permissions,
    creativeWidths: creativeTrackerV2.creativeWidths,
  };
}

export default connect(mapStateToProps)(CreativeTrackerV2);
